import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Briefcase, GraduationCap, Users, Globe, Mail
} from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── Careers Hero ─── */
function CareersHero() {
  return (
    <section className="bg-brand-navy py-20 lg:py-28" data-testid="careers-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4 font-source-sans">
              Careers
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-lexend font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              Grow your career at the{' '}
              <span className="text-brand-orange">forefront of technology</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-source-sans">
              We are actively growing our team of specialists. If you are a
              practitioner who thrives on deep, impactful work and brings with
              you a genuine curiosity to keep learning, a drive to innovate, and
              a belief that great outcomes come from great collaboration - we
              would love to hear from you.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Why Join iMane ─── */
function WhyJoin() {
  const cards = [
    {
      icon: Briefcase,
      title: 'Work on Complex Engagements',
      desc: 'No two clients are the same. Our projects span industries, geographies, and modules - keeping the work genuinely challenging.',
    },
    {
      icon: GraduationCap,
      title: 'Senior Mentorship',
      desc: 'Every team member works directly with founders and senior practitioners. Learning happens through doing, not watching.',
    },
    {
      icon: Users,
      title: 'Boutique Culture',
      desc: '26–50 people. Everyone knows everyone. Decisions are made quickly and autonomously. No corporate hierarchy.',
    },
    {
      icon: Globe,
      title: 'Global Exposure',
      desc: 'Our client base spans India, APAC, Singapore, and Australia/NZ - giving team members rare exposure to international enterprise procurement environments.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="why-join">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              Why Join iMANE?
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 80}>
              <div
                className="bg-brand-bg rounded-xl p-8 border border-brand-border hover:border-brand-orange transition-colors duration-200"
                data-testid={`why-join-card-${i + 1}`}
              >
                <div className="w-12 h-12 rounded-lg bg-brand-orange-light flex items-center justify-center mb-5">
                  <c.icon size={22} className="text-brand-orange" />
                </div>
                <h3 className="font-lexend font-semibold text-xl text-brand-navy mb-3">
                  {c.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed font-source-sans">
                  {c.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Open Roles ─── */
function OpenRoles() {
  return (
    <section className="py-20 lg:py-28 bg-brand-orange" data-testid="open-roles">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-6">
              We Are Hiring
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-slate-800 text-base sm:text-lg leading-relaxed font-source-sans mb-6">
              We are selectively growing our team of specialists across implementation, integration, and consulting functions.
              If you have hands-on experience in supply chain and procurement technology and want to do
              the best work of your career - talk to us.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <a
              href="mailto:neha@imane.in?subject=Career Application"
              className="btn-orange inline-flex items-center gap-2"
              data-testid="open-roles-apply-link"
            >
              Send Your Application <Mail size={16} />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Apply via Email ─── */
function ApplyEmail() {
  return (
    <section className="py-20 bg-white border-t border-brand-border" data-testid="apply-email">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <Reveal>
            <h2 className="font-lexend font-bold text-2xl lg:text-3xl text-brand-navy mb-4">
              Don't See Your Role Listed?
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-slate-600 text-base font-source-sans leading-relaxed mb-6">
              We review speculative applications from strong procurement technology practitioners year-round.
              Send your CV and a brief note on your Ariba experience to:
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="bg-brand-bg rounded-xl p-7 border border-brand-border">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-brand-orange-light flex items-center justify-center flex-shrink-0">
                  <Mail size={22} className="text-brand-orange" />
                </div>
                <div>
                  <a
                    href="mailto:neha@imane.in?subject=Application - [Your Name] - [Area of Expertise]"
                    className="font-lexend font-bold text-xl text-brand-orange hover:text-brand-orange-dark transition-colors"
                    data-testid="apply-email-link"
                  >
                    neha@imane.in
                  </a>
                </div>
              </div>
              <p className="text-slate-600 text-sm font-source-sans">
                <span className="font-semibold text-slate-700">Subject line:</span>{' '}
                Application — [Your Name] — [Area of Expertise]
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Careers() {
  return (
    <div data-testid="careers-page">
      <Helmet>
        <title>Careers at iMANE | Join Our Procurement Team | Mane Labs LLP</title>
        <meta name="description" content="Join iMANE (Mane Labs LLP), a growing boutique procurement consulting firm. We are hiring certified supply chain and procurement specialists across India and APAC." />
        <meta name="keywords" content="iMANE careers, mane labs jobs, procurement consulting jobs India, SAP Ariba careers India, supply chain jobs, iMANE hiring" />
        <link rel="canonical" href="https://www.imane.in/careers" />
        <meta property="og:title" content="Careers at iMANE | Join Our Procurement Team | Mane Labs LLP" />
        <meta property="og:description" content="Join iMANE - a growing boutique procurement consulting firm. Hiring certified supply chain and procurement specialists." />
        <meta property="og:url" content="https://www.imane.in/careers" />
      </Helmet>
      <CareersHero />
      <WhyJoin />
      <OpenRoles />
      <ApplyEmail />
    </div>
  );
}
